import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";

import CalendriersStatsCards from "../components/calendriers/CalendriersStatsCards";
import CalendriersFiltersCard from "../components/calendriers/CalendriersFiltersCard";
import CalendriersTableCard from "../components/calendriers/CalendriersTableCard";
import CalendriersFormDialog from "../components/calendriers/CalendriersFormDialog";
import CalendriersDeleteDialog from "../components/calendriers/CalendriersDeleteDialog";

const DEFAULT_FORM = {
  name: "",
  year: new Date().getFullYear(),
  country: "Tunisie",
  is_template: false,
  holiday_ids: [],
};

const JoursFeriesCalendriers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  // Reset page on filter/search change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, yearFilter, countryFilter, statusFilter]);

  const [statsQ, calendarsQ] = useQueries({
    queries: [
      {
        queryKey: ['holiday-calendars-stats'],
        queryFn: () => apiClient.get("v1/holidays/stats/calendars").then(r => r.data),
      },
      {
        queryKey: ['holiday-calendars', currentPage, yearFilter, countryFilter, statusFilter, searchTerm],
        queryFn: async () => {
          const params = { skip: (currentPage - 1) * limit, limit };
          if (searchTerm) params.search = searchTerm;
          if (yearFilter !== "all") params.year = yearFilter;
          if (countryFilter !== "all") params.country = countryFilter;
          if (statusFilter !== "all") params.status = statusFilter;
          const r = await apiClient.get("v1/holidays/holiday-calendars", { params });
          return r.data;
        },
      },
    ],
  });

  // Available holidays for form dialog — driven by formData.year / formData.country
  const availableHolidaysQ = useQuery({
    queryKey: ['available-holidays-dialog', formData.year, formData.country],
    queryFn: async () => {
      const params = { year: formData.year, limit: 500 };
      if (formData.country) params.country = formData.country;
      const r = await apiClient.get("v1/holidays/public-holidays", { params });
      return r.data.holidays || [];
    },
    enabled: isCreateModalOpen || isEditModalOpen,
  });

  const stats = statsQ.data ?? null;
  const statsLoading = statsQ.isPending;
  const calendars = calendarsQ.data?.calendars ?? [];
  const totalCalendars = calendarsQ.data?.total ?? 0;
  const loading = calendarsQ.isPending;
  const availableHolidays = availableHolidaysQ.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['holiday-calendars'] });
    queryClient.invalidateQueries({ queryKey: ['holiday-calendars-stats'] });
  };

  const handleOpenCreateModal = () => {
    setFormData(DEFAULT_FORM);
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (calendar) => {
    setSelectedCalendar(calendar);
    setFormData({
      name: calendar.name,
      year: calendar.year,
      country: calendar.country,
      is_template: calendar.is_template,
      holiday_ids: [],
    });
    setIsEditModalOpen(true);
  };

  const handleCreateCalendar = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!formData.name.trim()) { toast.error(t("holidayCalendars.toasts.calendarNameRequired")); return; }
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.post("v1/holidays/holiday-calendars", formData);
      toast.success(t("holidayCalendars.toasts.createSuccess"));
      setIsCreateModalOpen(false);
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendars.toasts.createError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCalendar = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error(t("holidayCalendars.toasts.calendarNameRequired")); return; }
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const updateData = { name: formData.name, year: formData.year, country: formData.country, is_template: formData.is_template };
      await apiClient.put(`v1/holidays/holiday-calendars/${selectedCalendar.id}`, updateData);
      toast.success(t("holidayCalendars.toasts.updateSuccess"));
      setIsEditModalOpen(false);
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendars.toasts.updateError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveCalendar = async (calendarId) => {
    try {
      await apiClient.post(`v1/holidays/holiday-calendars/${calendarId}/approve`);
      toast.success(t("holidayCalendars.toasts.approveSuccess"));
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendars.toasts.approveError"));
    }
  };

  const handleDeleteCalendar = async () => {
    try {
      await apiClient.delete(`v1/holidays/holiday-calendars/${selectedCalendar.id}`);
      toast.success(t("holidayCalendars.toasts.deleteSuccess"));
      setIsDeleteDialogOpen(false);
      setSelectedCalendar(null);
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendars.toasts.deleteError"));
    }
  };

  const confirmDelete = (calendar) => {
    setSelectedCalendar(calendar);
    setIsDeleteDialogOpen(true);
  };

  const navigateToDetails = (calendarId) => {
    navigate(`/jours-feries/calendriers/${calendarId}`);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("holidayCalendars.title")}</h1>
          <p className="text-muted-foreground">{t("holidayCalendars.subtitle")}</p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <Plus className="mr-2 h-4 w-4" />
          {t("holidayCalendars.newCalendar")}
        </Button>
      </div>

      <CalendriersStatsCards stats={stats} statsLoading={statsLoading} />

      <CalendriersFiltersCard
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        yearFilter={yearFilter} setYearFilter={setYearFilter}
        countryFilter={countryFilter} setCountryFilter={setCountryFilter}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
      />

      <CalendriersTableCard
        loading={loading}
        calendars={calendars}
        handleOpenCreateModal={handleOpenCreateModal}
        navigateToDetails={navigateToDetails}
        handleOpenEditModal={handleOpenEditModal}
        handleApproveCalendar={handleApproveCalendar}
        confirmDelete={confirmDelete}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalCalendars={totalCalendars}
        limit={limit}
      />

      <CalendriersFormDialog
        isCreateModalOpen={isCreateModalOpen}
        setIsCreateModalOpen={setIsCreateModalOpen}
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        formData={formData}
        setFormData={setFormData}
        availableHolidays={availableHolidays}
        loadAvailableHolidays={() => {}}
        isSubmitting={isSubmitting}
        handleCreateCalendar={handleCreateCalendar}
        handleUpdateCalendar={handleUpdateCalendar}
      />

      <CalendriersDeleteDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        selectedCalendar={selectedCalendar}
        handleDeleteCalendar={handleDeleteCalendar}
      />
    </div>
  );
};

export default JoursFeriesCalendriers;
